import { useImperativeHandle, useRef, useState } from 'react';
import type { FormEvent, FormHTMLAttributes, Ref } from 'react';
import { useTranslation } from 'react-i18next';

import { EmptyDataMessage, NgButton, NgModal } from '@/ui';
import type { NgModalHandle } from '@/ui';
import { useEditForm } from '@/ui/forms/useEditForm';
import { manualPositionUpdate } from '../actions/emsCommands';
import { MAPY_CZ_POSITION_TYPE_COORDINATE, searchAddress } from '../api/portalIncidentApi';
import type { AddressSearchResultItem } from '../api/portalIncidentApi';
import { formatCoordinatesToDegree } from '../model/formatters';

const FORM_ID = `form-${crypto.randomUUID()}`;
const VALUE_INPUT_ID = 'location-search';

export type ManualGpsCoordinatesDialogHandle = {
  showModal: () => void;
};

export type ManualGpsCoordinatesDialogProps = {
  incidentId: string;
  ref?: Ref<ManualGpsCoordinatesDialogHandle>;
};

function ManualGpsCoordinatesSearchResultItem({
  item,
  onClick,
}: {
  item: AddressSearchResultItem;
  onClick: (item: AddressSearchResultItem) => void | Promise<void>;
}) {
  const { t } = useTranslation();

  const itemTitle =
    item.type === MAPY_CZ_POSITION_TYPE_COORDINATE ? formatCoordinatesToDegree(item.position) : item.name;
  const itemSubtitle = `${item.label}, ${item.location}`;

  return (
    <div
      className="list-group-item list-group-item-action p-2"
      style={{ cursor: 'pointer' }}
      onClick={() => void onClick(item)}
    >
      <div className="d-flex justify-content-between align-items-center gap-2">
        <div>
          <div>{itemTitle}</div>
          <div className="small">{itemSubtitle}</div>
        </div>
        <div className="text-bg-primary px-2 py-1 rounded-1">{t('Button_Apply')}</div>
      </div>
    </div>
  );
}

export function ManualGpsCoordinatesDialog({ incidentId, ref }: ManualGpsCoordinatesDialogProps) {
  const { t, i18n } = useTranslation();
  const modalRef = useRef<NgModalHandle>(null);

  const [query, setQuery] = useState('');
  const [inProgress, setInProgress] = useState(false);
  const [searchResults, setSearchResults] = useState<AddressSearchResultItem[] | null>(null);
  const editForm = useEditForm<'query'>();

  const resetForm = () => {
    setQuery('');
    setSearchResults(null);
    editForm.reset({ query: '' });
  };

  useImperativeHandle(ref, () => ({
    showModal: () => {
      resetForm();
      modalRef.current?.show();
    },
  }));

  const closeDialog = () => {
    modalRef.current?.hide();
    resetForm();
  };

  const formSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    editForm.validate(() => ({}));

    setInProgress(true);
    setSearchResults(null);
    const language = (i18n.language || 'en').split('-')[0] ?? 'en';
    try {
      setSearchResults(await searchAddress(query, language));
    } catch (error) {
      console.error('[ManualGpsCoordinatesDialog] Address search failed', error);
      setSearchResults([]);
    } finally {
      setInProgress(false);
    }
  };

  const useLocation = async (item: AddressSearchResultItem) => {
    await manualPositionUpdate(incidentId, item.position);
    closeDialog();
  };

  const formDisabledAttribute = (
    inProgress ? { disabled: 'disabled' } : {}
  ) as unknown as FormHTMLAttributes<HTMLFormElement>;

  return (
    <NgModal
      ref={modalRef}
      header={t('EditGpsLocationDialog_SetLocationManually_Header')}
      bodyTemplate={
        <>
          <form id={FORM_ID} onSubmit={(event) => void formSubmit(event)} {...formDisabledAttribute}>
            <div>
              <label htmlFor={VALUE_INPUT_ID} className="label">
                {t('EditGpsLocationDialog_AddressOrGpsCoordinates_Label')}
              </label>

              <div className="input-group">
                <input
                  id={VALUE_INPUT_ID}
                  className={editForm.fieldCssClass('query', 'form-control')}
                  value={query}
                  onChange={(event) => {
                    setQuery(event.target.value);
                    editForm.notifyFieldInput('query', event.target.value);
                  }}
                  onBlur={() => editForm.notifyFieldBlur('query')}
                />
                <button type="submit" className="btn btn-primary" disabled={inProgress}>
                  {inProgress ? <i className="fal fa-spinner fa-spin"></i> : <i className="fal fa-search"></i>}
                </button>
              </div>
              {editForm.validationMessages('query').map((message, index) => (
                <div key={index} className="validation-message">
                  {message}
                </div>
              ))}
            </div>
          </form>

          {searchResults !== null &&
            (searchResults.length > 0 ? (
              <div className="list-group rounded-1 mt-2">
                {searchResults.map((item, index) => (
                  <ManualGpsCoordinatesSearchResultItem key={index} item={item} onClick={useLocation} />
                ))}
              </div>
            ) : (
              <EmptyDataMessage text={t('EditGpsLocationDialog_NoResultsMessage')} />
            ))}
        </>
      }
      footerTemplate={<NgButton text={t('Button_Cancel')} outline onClick={closeDialog} />}
    />
  );
}
